/**
 * k-way merge pagination utility for sales feeds that pull from multiple
 * sources with independent cursors.
 *
 * The collection Activity and address Sales tabs both blend two streams:
 *   - OW marketplace fills  (richer per-row data, offset-paginated)
 *   - Cross-marketplace fills from the local ord classifier  (cursor-paginated)
 *
 * Each source returns rows newest-first. The naive merge — sort the union by
 * timestamp DESC — produces visual jitter as the user pages: a fresh page
 * from the "slow" source can introduce rows that belong CHRONOLOGICALLY in
 * the middle of what the user has already scrolled past, so rows appear to
 * pop into the middle of the list.
 *
 * The fix is the standard k-way merge watermark: only render rows newer
 * than the OLDEST loaded-so-far row of any not-yet-exhausted source. New
 * rows then always append at the bottom — never insert above the user's
 * current scroll position. When the user scrolls past the watermark,
 * `nextSource` tells the caller which source to advance to push the
 * watermark further back in time.
 *
 * Sources can be "unbounded" (single-shot fetch returning everything, e.g.
 * `useAddressSales` which returns all OW sales for an address in one call).
 * Mark those with `done: true` once their data is loaded and they'll be
 * treated as exhausted (no longer holding back the watermark).
 *
 * Sources still loading their first page have an `oldestTs` of `Infinity`
 * → that forces the watermark to also be `Infinity` and nothing renders
 * until both sources have responded. That's intentional: rendering only
 * one source's first page before the other has loaded would put us in the
 * exact "rows pop into the middle" failure mode.
 */

export interface MergeSource {
  /// Timestamp (ms epoch) of the oldest row loaded so far from this source.
  /// Use `Infinity` before the first page resolves; the watermark stays at
  /// `Infinity` until every source has produced at least one row.
  oldestTs: number
  /// True once this source has no more rows to fetch (either offset-paged
  /// past the end OR was a single-shot fetch that returned everything).
  /// Exhausted sources stop holding back the watermark.
  done: boolean
  /// True while a `fetchMore()` is in flight. The caller can use this to
  /// suppress duplicate fetches while one is pending.
  isLoadingMore: boolean
}

export interface MergeResult {
  /// Rows with `timestamp_ms >= safeBoundaryMs` can be rendered in stable
  /// chronological order. Anything older belongs in the "uncertain tail"
  /// — wait for the lagging source to catch up before showing it.
  safeBoundaryMs: number
  /// Identifies which source is holding back the watermark and should be
  /// advanced when the user scrolls past the current safe boundary.
  /// `null` when every source is exhausted (no more pagination possible).
  laggingSource: 'ow' | 'global' | 'both' | null
  /// True when the watermark can still move backward in time (at least one
  /// source is not yet exhausted). The UI should render an InfiniteScroll
  /// "more" loader when this is true and the user has scrolled to the
  /// bottom of `visible` rows.
  canPageMore: boolean
}

/**
 * Compute the k-way merge watermark for the two sale sources.
 *
 * Treats exhausted sources as `-Infinity` (they can no longer surface a
 * row older than the watermark, so they don't hold it back). The
 * watermark is the MAX of the active sources' `oldestTs` — the source
 * whose `oldestTs` equals that max is the one we still need to drain
 * before older rows from the other source become safe to render.
 *
 * Tie semantics: if both sources have the same `oldestTs` (e.g. both
 * fetched to the same date or both still loading their first page),
 * `laggingSource` is `'both'` and the caller should advance both.
 */
export function computeMergeWatermark(
  ow: MergeSource,
  global: MergeSource,
): MergeResult {
  const owEffective = ow.done ? Number.NEGATIVE_INFINITY : ow.oldestTs
  const globalEffective = global.done ? Number.NEGATIVE_INFINITY : global.oldestTs

  // Both done — no more paging, render everything we have.
  if (ow.done && global.done) {
    return {
      safeBoundaryMs: Number.NEGATIVE_INFINITY,
      laggingSource: null,
      canPageMore: false,
    }
  }

  const safeBoundaryMs = Math.max(owEffective, globalEffective)

  // Watermark = whoever has loaded the LEAST. They're the lagging source.
  let laggingSource: 'ow' | 'global' | 'both'
  if (ow.done) {
    laggingSource = 'global'
  } else if (global.done) {
    laggingSource = 'ow'
  } else if (owEffective === globalEffective) {
    laggingSource = 'both'
  } else if (owEffective > globalEffective) {
    laggingSource = 'ow'
  } else {
    laggingSource = 'global'
  }

  return {
    safeBoundaryMs,
    laggingSource,
    canPageMore: true,
  }
}

/**
 * Convenience helper that derives the oldest timestamp from a row array,
 * returning `Infinity` for the empty/undefined case (i.e. still loading
 * — that propagates through `computeMergeWatermark` to suppress all
 * rendering until first pages arrive).
 */
export function oldestTimestampMs<T>(
  rows: T[] | undefined,
  getMs: (row: T) => number,
): number {
  if (!rows || rows.length === 0) return Number.POSITIVE_INFINITY
  let oldest = Number.POSITIVE_INFINITY
  for (const r of rows) {
    const t = getMs(r)
    // Ignore non-positive / non-finite timestamps (e.g. a row with a
    // missing `bought_at` → 0, or a malformed `block_timestamp`). A single
    // such row must NOT collapse the watermark to 0, which would prematurely
    // admit the entire other source and reintroduce the mid-list "pop" this
    // watermark exists to prevent.
    if (Number.isFinite(t) && t > 0 && t < oldest) oldest = t
  }
  return oldest
}

/**
 * Stable de-duplication by a string identity key, preserving first-seen order.
 *
 * Cursor-paginated sale sources page on `before_height`, which is block-granular
 * — but a single block routinely holds multiple sales. If the server's cursor
 * comparator is inclusive (`<=`), or a dense block straddles a page boundary,
 * the boundary block's rows come back on two consecutive pages. Flattening the
 * pages then yields duplicate rows (duplicate React keys + a visibly doubled
 * sale). De-duping by a content identity (txid + inscription_id [+ role])
 * collapses those regardless of the backend's comparator semantics.
 */
export function dedupeByKey<T>(rows: T[], key: (row: T) => string): T[] {
  const seen = new Set<string>()
  const out: T[] = []
  for (const r of rows) {
    const k = key(r)
    if (seen.has(k)) continue
    seen.add(k)
    out.push(r)
  }
  return out
}
